import React from 'react';
import { motion } from 'framer-motion';
import { FadeInUp, AnimatedCounter } from './AnimationWrappers';
import { ChevronDown } from 'lucide-react';

const METRICS = [
  { value: '30', suffix: '+', label: 'Years of Precision' },
  { value: '45000', suffix: '', label: 'Sq. Ft. Facility' },
  { value: '400', suffix: '+', label: 'Skilled Workforce' },
];

const HeroSection = () => {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative overflow-hidden flex items-end"
      style={{ background: '#1C1C1E', minHeight: '100vh', paddingBottom: '50px' }}
    >
      {/* Full bleed background image — positioned to show the jacket on the right */}
      <div className="absolute inset-0">
        <img
          src="/hero-banner.jpg"
          alt="Swiss military tactical jacket"
          className="w-full h-full object-cover"
          loading="eager"
          style={{ objectPosition: 'center top' }}
        />
        {/* Gradient overlays — heavier on left for text readability, lighter on right to show jacket */}
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(to right, rgba(28,28,30,0.88) 0%, rgba(28,28,30,0.65) 50%, rgba(28,28,30,0.3) 100%)'
        }} />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(to top, rgba(28,28,30,0.9) 0%, rgba(28,28,30,0.2) 40%, transparent 70%)'
        }} />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(to bottom, rgba(28,28,30,0.6) 0%, transparent 20%)'
        }} />
      </div>

      <div className="relative z-10 container-main w-full">
        {/* Badge */}
        <FadeInUp>
          <div className="inline-flex items-center gap-2 mb-8 px-4 py-2" style={{ border: '1px solid rgba(255,255,255,0.12)', borderRadius: '4px' }}>
            <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#C8102E' }} />
            <span className="font-mono" style={{ fontSize: '0.6rem', letterSpacing: '0.25em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)' }}>
              Manufacturing Laboratory
            </span>
          </div>
        </FadeInUp>

        {/* Headline */}
        <FadeInUp delay={0.1}>
          <h1 className="font-display text-white leading-none mb-6" style={{
            fontSize: 'clamp(2.5rem, 5vw, 4.2rem)',
            fontWeight: 700,
            letterSpacing: '-0.02em',
          }}>
            Apparel Engineering
            <br />
            at Its <span style={{ color: '#C8102E', display: 'inline', verticalAlign: 'baseline' }}>Peak.</span>
          </h1>
        </FadeInUp>

        {/* Subtitle */}
        <FadeInUp delay={0.2}>
          <p className="font-body mb-12" style={{
            fontSize: '1.15rem',
            fontWeight: 300,
            color: 'rgba(255, 255, 255, 0.75)',
            maxWidth: '480px',
            lineHeight: 1.7,
          }}>
            Technical garment manufacturing engineered to Swiss military specifications.
            More than three decades of precision. One relentless standard.
          </p>
        </FadeInUp>

        {/* Metrics */}
        <FadeInUp delay={0.35}>
          <div
            className="flex flex-wrap gap-10 md:gap-14 pt-6"
            style={{ borderTop: '1px solid rgba(255, 255, 255, 0.15)' }}
          >
            {METRICS.map((m, i) => (
              <div key={i} className="flex flex-col" data-testid={`hero-metric-${i}`}>
                <span className="font-display text-white" style={{ fontSize: '1.8rem', fontWeight: 700 }}>
                  <AnimatedCounter target={m.value} suffix={m.suffix} />
                </span>
                <span className="font-mono mt-1" style={{
                  fontSize: '0.65rem',
                  color: 'rgba(255, 255, 255, 0.4)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.12em',
                }}>
                  {m.label}
                </span>
              </div>
            ))}
          </div>
        </FadeInUp>
      </div>

      {/* Scroll indicator */}
      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
        className="absolute bottom-5 left-1/2 -translate-x-1/2 z-10"
      >
        <ChevronDown size={18} style={{ color: 'rgba(255,255,255,0.25)' }} />
      </motion.div>
    </section>
  );
};

export default HeroSection;
